/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';
import objectHash from 'object-hash';

import CommunicatorUtility from '../communicator/utility/communicator-utility';

/**
 * Komunikátor přehrávaných stop playlistu pro Ki-Wi Server.
 */
class TrackCommunicator {
  /**
   * @param {TrackPlanCommunicator} trackPlanCommunicator
   */
  constructor(trackPlanCommunicator) {
    this.trackPlanCommunicator = trackPlanCommunicator;
  }

  /**
   * Zpracuje seznam stop a zanořených modelů.
   * @param {Array.<Object>} jsonArray Část odpovědi na obsah zařízení se seznamem stop.
   * @param {DsId} parentPlaylistId Identifikátor playlistu, do kterého stopa patří.
   * @returns {DsMap} Mapa datových atributů ke vložení do datového skladu.
   */
  parseMany(jsonArray, parentPlaylistId) {
    return CommunicatorUtility.parseMany(
      jsonArray,
      (jsonObject, index) => this.parseOne(jsonObject, parentPlaylistId, index),
      { track: {} }
    );
  }

  /**
   * Zpracuje jednu stopu bez zanořených modelů.
   * @param {Object} json Část odpovědi na obsah zařízení s jednou stopou.
   * @param {DsId} parentPlaylistId Identifikátor playlistu, do kterého stopa patří.
   * @param {number} index Pořadí stopy v rodičovském playlistu.
   * @private
   * @returns {TrackAttrs}
   */
  parseModel(json, parentPlaylistId, index) {
    if (isNil(parentPlaylistId)) {
      throw new TypeError('Missing parent playlist.');
    }
    if (isNil(index)) {
      throw new TypeError('Missing index.');
    }
    // Načte se seznam položek `TrackPlan` ve formátu DHTMLX Scheduler.
    const trackPlans = json.trackPlans ?? [];
    return {
      childPlaylistId: CommunicatorUtility.parseReference(
        json.ref,
        'playlist',
        { nil: null }
      ),
      // TODO V datech chybí id.
      // - Bude docházet ke zbytečným změnám při změně pořadí.
      // - Šlo by vylepšit navázáním na childPlaylistId nebo mediumId, ale ty mohou být duplikované.
      // - Tato duplikace by se ještě musela ošetřit např. identifikátor sestavit jako 1. mediumId v daném seznamu.
      id: objectHash({ parentPlaylistId, index }),
      index,
      layoutId: CommunicatorUtility.parseReference(json.ref, 'layout', {
        nil: null
      }),
      mediumId: CommunicatorUtility.parseReference(json.ref, 'media', {
        nil: null
      }),
      parentPlaylistId,
      // Převedou se události z DHTMLX do iCalendar formátu.
      planIcal: this.trackPlanCommunicator.parseIcal(trackPlans),
      playDuration: CommunicatorUtility.parseTime(json.schedule.duration),
      volume: CommunicatorUtility.parseVolume(json.schedule.volume)
    };
  }

  /**
   * Zpracuje jednu stopu a zanořené modely.
   * @param {Object} json Část odpovědi na obsah zařízení s jednou stopou.
   * @param {DsId} parentPlaylistId Identifikátor playlistu, do kterého stopa patří.
   * @param {number} index Pořadí stopy v rodičovském playlistu.
   * @private
   * @returns {DsMap} Mapa datových atributů ke vložení do datového skladu.
   */
  parseOne(json, parentPlaylistId, index) {
    // Vytvoří se stopa playlistu.
    const track = this.parseModel(json, parentPlaylistId, index);
    // Vytvoří se mapa se stopami playlistu.
    const trackMap = { [track.id]: track };
    return { track: trackMap };
  }
}

export default TrackCommunicator;
